import type { SimConfig } from '../config';
import type { EventSink } from '../events';
import { computeCross, inCross, isFreeCell } from '../geometry';
import { nextDiagonalDirection, nextInt } from '../rng';
import type { GoldenBallState, MatchState } from '../types';

const OVERLAP_MARGIN = 0.1;

interface CellFlags {
  col: number;
  row: number;
  overlap: boolean;
  inLethal: boolean;
  forbiddenRow: boolean;
  playerDistance: number;
  killZone: boolean;
  wrongRegion: boolean;
}

/**
 * Relaxation levels, strictest first. A ball must never appear
 * overlapping something; every other constraint can be relaxed progressively.
 */
const LEVELS: readonly ((f: CellFlags, minDistance: number) => boolean)[] = [
  (f, d) =>
    !f.overlap &&
    !f.inLethal &&
    !f.forbiddenRow &&
    !f.killZone &&
    !f.wrongRegion &&
    f.playerDistance >= d,
  (f, d) => !f.overlap && !f.inLethal && !f.forbiddenRow && !f.killZone && f.playerDistance >= d,
  (f, d) =>
    !f.overlap && !f.inLethal && !f.forbiddenRow && !f.killZone && f.playerDistance >= d / 2,
  (f, d) => !f.overlap && !f.inLethal && !f.forbiddenRow && f.playerDistance >= d / 2,
  (f) => !f.overlap && !f.inLethal,
  (f) => !f.overlap,
  () => true,
];

export interface SpawnChoice {
  col: number;
  row: number;
  relaxation: number;
}

export function evaluateSpawnCells(state: MatchState, config: SimConfig, self: GoldenBallState) {
  const flags: CellFlags[] = [];
  const gr = config.golden.radius;
  const half = config.rows / 2;
  for (let row = 0; row < config.rows; row++) {
    for (let col = 0; col < config.columns; col++) {
      if (!isFreeCell(config, col, row)) continue;
      const x = col + 0.5;
      const y = row + 0.5;
      let overlap = false;
      let playerDistance = Number.POSITIVE_INFINITY;
      for (const p of state.players) {
        const d = Math.sqrt((p.x - x) ** 2 + (p.y - y) ** 2);
        playerDistance = Math.min(playerDistance, d);
        if (d < config.player.radius + gr + OVERLAP_MARGIN) overlap = true;
      }
      for (const g of state.goldens) {
        if (g === self || g.status === 'ABSENT') continue;
        const d = Math.sqrt((g.x - x) ** 2 + (g.y - y) ** 2);
        if (d < 2 * gr + OVERLAP_MARGIN) overlap = true;
      }
      const inLethal = state.explosions.some(
        (e) => state.tick < e.lethalUntilTick && inCross(e.cross, x, y),
      );
      const killZone = computeCross(config, col, row, config.explosion.range).basesHit.length > 0;
      const topHalf = y < half;
      flags.push({
        col,
        row,
        overlap,
        inLethal,
        forbiddenRow: config.map.forbiddenSpawnRows.includes(row),
        playerDistance,
        killZone,
        wrongRegion: state.spawnRegion === 0 ? !topHalf : topHalf,
      });
    }
  }
  return flags;
}

export function chooseSpawnCell(
  state: MatchState,
  config: SimConfig,
  self: GoldenBallState,
): SpawnChoice {
  const flags = evaluateSpawnCells(state, config, self);
  const minDistance = config.respawn.minPlayerDistance;
  for (let level = 0; level < LEVELS.length; level++) {
    const accept = LEVELS[level] as (f: CellFlags, d: number) => boolean;
    const candidates = flags.filter((f) => accept(f, minDistance));
    if (candidates.length > 0) {
      const pick = candidates[nextInt(state, candidates.length)] as CellFlags;
      return { col: pick.col, row: pick.row, relaxation: level };
    }
  }
  throw new Error('Map has no free cell');
}

/** ABSENT -> MATERIALISING (intangible, 1 s) -> NEUTRAL. */
export function resolveRespawns(state: MatchState, config: SimConfig, sink: EventSink) {
  const tick = sink.tick;
  for (const golden of state.goldens) {
    if (
      golden.status === 'ABSENT' &&
      golden.respawnAtTick !== null &&
      tick >= golden.respawnAtTick
    ) {
      const choice = chooseSpawnCell(state, config, golden);
      state.spawnRegion = state.spawnRegion === 0 ? 1 : 0;
      golden.status = 'MATERIALISING';
      golden.x = choice.col + 0.5;
      golden.y = choice.row + 0.5;
      golden.vx = 0;
      golden.vy = 0;
      golden.respawnAtTick = null;
      golden.activeAtTick = tick + config.ticks.materialise;
      sink.emit({
        type: 'GOLDEN_MATERIALISING',
        goldenId: golden.id,
        col: choice.col,
        row: choice.row,
        relaxation: choice.relaxation,
      });
    } else if (
      golden.status === 'MATERIALISING' &&
      golden.activeAtTick !== null &&
      tick >= golden.activeAtTick
    ) {
      const dir = nextDiagonalDirection(state, config.golden.minAxisAngleDegrees);
      golden.status = 'NEUTRAL';
      golden.vx = dir.x * config.golden.cruiseSpeed;
      golden.vy = dir.y * config.golden.cruiseSpeed;
      golden.activeAtTick = null;
      sink.emit({ type: 'GOLDEN_SPAWNED', goldenId: golden.id });
    }
  }
}
